/**
 * Idle-session driver (brief `tasks/prompt-auto-logout-dashboard.md`).
 *
 * - Interaction marks (pointer, keyboard, scroll, touch, return to the
 *   tab) go to localStorage so every tab of the session agrees.
 * - A one-second tick reads the newest mark and derives the phase: active,
 *   warning (dialog with the countdown), expired (logout). The countdown is
 *   the remaining time itself, so it survives a throttled background tab.
 * - Every 30 s a keepalive decision: an active user whose JWT deadline has
 *   passed gets one `GET /auth/token/metadata`; the exchange happens
 *   inside that call and the response carries the next deadline.
 * - While the dialog is open, page activity is ignored: only "Stay logged
 *   in" (a successful keepalive) or another tab's activity closes it.
 * - `isUserIdle` (no interaction for min(5 min, timeout)) goes to Redux for
 *   the journey poller and the alerts hub.
 * - A logout in another tab arrives as the `isLogin` storage key turning
 *   false; this tab follows with the same hash.
 * - Once the logout started the hook stands down, but not for good: a
 *   page still alive LOGOUT_RETRY_MS later did not get away (a frozen
 *   background tab loses the navigation along with everything else),
 *   and the next tick or return to the tab starts the logout again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { client } from '@shared/api/client';
import {
  IS_LOGIN_STORAGE_KEY,
  KEEPALIVE_CHECK_MS,
  TICK_MS,
  TOUCH_THROTTLE_MS,
  readIdleTimeoutMs,
  readLastInteractionAt,
  readLastKeepaliveAt,
  readLogoutHash,
  readTokenDeadlineAt,
  writeLastInteractionAt,
  writeLastKeepaliveAt,
  writeLogoutHash,
} from '@shared/lib/idleSession';
import { sessionKeptAlive, setAutoLogoutDialogOpen, setUserIdle } from 'redux_store';
import {
  IDLE_PHASE,
  idlePhase,
  isUserActive,
  resolveIdleTimeoutMs,
  shouldKeepAlive,
} from '../lib/idleSchedule';
import { LOGOUT_HASH, logoutSession } from './logoutSession';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];

// How long a started logout is trusted to take the page away.
export const LOGOUT_RETRY_MS = 5000;
const LISTENER_OPTIONS = { passive: true, capture: true };

// Lightweight authenticated request; 410 is handled here, not by the
// client's global redirect, so the login page gets the idle message.
const keepAliveRequest = dispatch =>
  client.get('/auth/token/metadata', undefined, { dispatch, silent410: true });

export const useIdleSession = ({ enabled }) => {
  const dispatch = useDispatch();
  const dialogOpen = useSelector(state => state.appSettings.isAutoLogoutDialogOpen);
  const isUserIdle = useSelector(state => state.appSettings.isUserIdle);
  const [remainingMs, setRemainingMs] = useState(null);

  // Timers and listeners read the latest values through this ref.
  const latest = useRef({ enabled, dialogOpen, isUserIdle });
  latest.current = { enabled, dialogOpen, isUserIdle };
  const lastTouchRef = useRef(0);
  const endedRef = useRef(false);
  const endedAtRef = useRef(0);
  // A logout is in progress and still expected to take the page away.
  const ending = useCallback(
    () => endedRef.current && Date.now() - endedAtRef.current < LOGOUT_RETRY_MS,
    []
  );

  // localStorage is the one source of truth for the last interaction —
  // every tab writes it, and a tester can move it. The in-memory mark
  // only stands in when storage is unavailable.
  const marks = useCallback(() => {
    const stored = readLastInteractionAt();
    const lastInteractionAt = stored === null ? lastTouchRef.current || null : stored;
    return { lastInteractionAt, idleTimeoutMs: resolveIdleTimeoutMs(readIdleTimeoutMs()) };
  }, []);

  const markInteraction = useCallback(
    now => {
      lastTouchRef.current = now;
      writeLastInteractionAt(now);
      if (latest.current.dialogOpen) dispatch(setAutoLogoutDialogOpen(false));
      if (latest.current.isUserIdle) dispatch(setUserIdle(false));
    },
    [dispatch]
  );

  const touch = useCallback(() => {
    if (!latest.current.enabled || endedRef.current || latest.current.dialogOpen) return;
    const now = Date.now();
    if (now - lastTouchRef.current < TOUCH_THROTTLE_MS) return;
    markInteraction(now);
  }, [markInteraction]);

  const end = useCallback(
    hash => {
      if (ending()) return;
      endedRef.current = true;
      endedAtRef.current = Date.now();
      dispatch(setAutoLogoutDialogOpen(false));
      logoutSession(dispatch, hash);
    },
    [dispatch, ending]
  );

  const tick = useCallback(() => {
    if (!latest.current.enabled) return;
    if (endedRef.current) {
      // Still here after the logout should have left: ask again.
      if (!ending()) end(LOGOUT_HASH.auto);
      return;
    }
    const now = Date.now();
    const { lastInteractionAt, idleTimeoutMs } = marks();
    const { phase, remainingMs: left } = idlePhase({ now, lastInteractionAt, idleTimeoutMs });
    if (phase === IDLE_PHASE.expired) {
      end(LOGOUT_HASH.auto);
      return;
    }
    setRemainingMs(phase === IDLE_PHASE.warning ? left : null);
    if (phase === IDLE_PHASE.warning && !latest.current.dialogOpen) {
      dispatch(setAutoLogoutDialogOpen(true));
    }
    if (phase === IDLE_PHASE.active && latest.current.dialogOpen) {
      dispatch(setAutoLogoutDialogOpen(false));
    }
    const idle = !isUserActive({ now, lastInteractionAt, idleTimeoutMs });
    if (idle !== latest.current.isUserIdle) dispatch(setUserIdle(idle));
  }, [dispatch, end, ending, marks]);

  const keepAliveIfDue = useCallback(async () => {
    if (!latest.current.enabled || endedRef.current) return;
    const now = Date.now();
    const due = shouldKeepAlive({
      now,
      ...marks(),
      tokenDeadlineAt: readTokenDeadlineAt(),
      lastKeepaliveAt: readLastKeepaliveAt(),
    });
    if (!due) return;
    writeLastKeepaliveAt(now);
    try {
      const response = await keepAliveRequest(dispatch);
      if (response.status === 410) end(LOGOUT_HASH.auto);
      else if (response.ok) dispatch(sessionKeptAlive());
    } catch (_) {
      // Network failure: the next user-driven request does the exchange.
    }
  }, [dispatch, end, marks]);

  // "Stay logged in": one authenticated request; 2xx resets the timer.
  const stay = useCallback(async () => {
    if (endedRef.current) return;
    try {
      const response = await keepAliveRequest(dispatch);
      if (response.status === 410) {
        end(LOGOUT_HASH.auto);
        return;
      }
      if (response.ok) {
        markInteraction(Date.now());
        writeLastKeepaliveAt(Date.now());
        dispatch(sessionKeptAlive());
      }
    } catch (_) {
      // Keep the dialog and its countdown; the user can try again.
    }
  }, [dispatch, end, markInteraction]);

  const logOut = useCallback(() => end(LOGOUT_HASH.manual), [end]);

  useEffect(() => {
    if (!enabled) {
      endedRef.current = false;
      setRemainingMs(null);
      return undefined;
    }
    // A fresh sign-in (login.fulfilled marks the interaction) or a reload
    // mid-session; an older build without a mark starts counting now.
    writeLogoutHash(null);
    if (readLastInteractionAt() === null) writeLastInteractionAt(Date.now());

    const onActivity = () => touch();
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // A logout that started before the tab was frozen never got away:
      // start it again now that the tab is back.
      if (endedRef.current) {
        if (!ending()) end(LOGOUT_HASH.auto);
        return;
      }
      // The browser may have throttled the timers of a hidden tab: judge
      // the stored mark first, then count the return as an interaction.
      const now = Date.now();
      const { phase } = idlePhase({ now, ...marks() });
      if (phase === IDLE_PHASE.expired) end(LOGOUT_HASH.auto);
      else touch();
    };
    const onStorage = event => {
      if (event.key !== IS_LOGIN_STORAGE_KEY || event.newValue !== 'false' || endedRef.current) {
        return;
      }
      endedRef.current = true;
      dispatch(setAutoLogoutDialogOpen(false));
      window.location.assign(`/login${readLogoutHash() || LOGOUT_HASH.manual}`);
    };

    ACTIVITY_EVENTS.forEach(name => window.addEventListener(name, onActivity, LISTENER_OPTIONS));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    const tickId = setInterval(tick, TICK_MS);
    const keepAliveId = setInterval(keepAliveIfDue, KEEPALIVE_CHECK_MS);
    tick();

    return () => {
      ACTIVITY_EVENTS.forEach(name =>
        window.removeEventListener(name, onActivity, LISTENER_OPTIONS)
      );
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      clearInterval(tickId);
      clearInterval(keepAliveId);
    };
  }, [enabled, dispatch, end, ending, keepAliveIfDue, marks, tick, touch]);

  return { remainingMs, stay, logOut };
};
